/**
 * Decodes the primary audio track of a media file into one `AudioBuffer`.
 *
 * Uses mediabunny (WebCodecs) to stream-decode the file, so multi-GB videos are never read into
 * memory as a whole — only the decoded PCM is kept. Falls back to `decodeAudioData` on the whole
 * file when mediabunny cannot read the audio (e.g. an unknown container); that fallback is
 * skipped for very large inputs.
 *
 * Sample positions follow media timestamps (sample 0 = media time 0), matching how video frames
 * are addressed, so audio and picture stay aligned even for files whose audio starts late.
 */
import { ALL_FORMATS, AudioBufferSink, Input } from 'mediabunny';
import type { MediaInput } from '../types.ts';
import { describeInput, inputBlob, sourceFor } from '../media/input.ts';

/** Inputs larger than this are not loaded whole for the `decodeAudioData` fallback. */
const FALLBACK_MAX_BYTES = 512 * 1024 ** 2;
/** WebAudio handles at most 32 channels; more than 8 is never needed for a mix. */
const MAX_CHANNELS = 8;

/** Thrown when the input has no audio track at all (not an error for the caller). */
export class NoAudioError extends Error {
  constructor(input: MediaInput) {
    super(`${describeInput(input)} has no audio track`);
    this.name = 'NoAudioError';
  }
}

export async function decodeAudio(
  input: MediaInput,
  fallbackDecoder: BaseAudioContext,
): Promise<AudioBuffer> {
  const mbInput = new Input({ source: sourceFor(input), formats: ALL_FORMATS });
  try {
    let track;
    try {
      track = await mbInput.getPrimaryAudioTrack();
    } catch {
      track = undefined; // unreadable container → fallback below
    }
    if (track === null) throw new NoAudioError(input);
    if (track && (await track.canDecode())) {
      const end = await track.computeDuration();
      const channels = Math.min(MAX_CHANNELS, Math.max(1, track.numberOfChannels));
      const out = new AudioBuffer({
        length: Math.max(1, Math.ceil(end * track.sampleRate)),
        numberOfChannels: channels,
        sampleRate: track.sampleRate,
      });
      for await (const { buffer, timestamp } of new AudioBufferSink(track).buffers()) {
        copyInto(out, buffer, timestamp);
      }
      return out;
    }
  } finally {
    mbInput.dispose();
  }
  return fallback(input, fallbackDecoder);
}

/** Writes `chunk` into `out` at `timestamp` seconds (clipping at both ends). */
export function copyInto(out: AudioBuffer, chunk: AudioBuffer, timestamp: number): void {
  const offset = Math.round(timestamp * out.sampleRate);
  const skip = Math.max(0, -offset); // e.g. encoder priming before t=0
  const dest = Math.max(0, offset);
  const n = Math.min(chunk.length - skip, out.length - dest);
  if (n <= 0) return;
  for (let c = 0; c < out.numberOfChannels; c++) {
    // Mono sources are duplicated into every output channel.
    const src = chunk.getChannelData(Math.min(c, chunk.numberOfChannels - 1));
    out.copyToChannel(src.subarray(skip, skip + n), c, dest);
  }
}

async function fallback(input: MediaInput, decoder: BaseAudioContext): Promise<AudioBuffer> {
  if (typeof input !== 'string' && input.size > FALLBACK_MAX_BYTES) {
    throw new Error(`The audio of ${describeInput(input)} cannot be decoded by this browser`);
  }
  const blob = await inputBlob(input);
  if (blob.size > FALLBACK_MAX_BYTES) {
    throw new Error(`The audio of ${describeInput(input)} cannot be decoded by this browser`);
  }
  return decoder.decodeAudioData(await blob.arrayBuffer());
}
